/**
 * File picker that uploads immediately to private storage and reports the
 * stored key. PDFs allowed. On touch devices that have a camera (phones, tablets) it also
 * offers "Tomar foto", which opens the rear camera directly (input capture="environment").
 */

import { useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle2, FileText, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { compressImageForUpload } from '../compressImage';
import { uploadRegistrationDocument } from '../publicRegistrationRepository';
import type { DocumentKind } from '../types';

interface DocumentUploadFieldProps {
  eventId: string;
  kind: DocumentKind;
  label: string;
  hint?: string;
  value: string | undefined;
  onChange: (key: string | undefined) => void;
  disabled?: boolean;
  /** Not required: no asterisk */
  optional?: boolean;
  /** Custom uploader (e.g. through a personal edit link); defaults to the public upload by event */
  upload?: (kind: DocumentKind, file: Blob) => Promise<string>;
}

/** Touch device with at least one camera: the only case where `capture` opens the camera app */
function useHasCamera(): boolean {
  const [hasCamera, setHasCamera] = useState(false);
  useEffect(() => {
    const touch = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    if (!touch) return;
    let cancelled = false;
    const devices = navigator.mediaDevices?.enumerateDevices;
    if (!devices) {
      setHasCamera(true); // can't tell: capture is harmless (falls back to the file picker)
      return;
    }
    navigator.mediaDevices
      .enumerateDevices()
      .then((list) => !cancelled && setHasCamera(list.some((d) => d.kind === 'videoinput')))
      .catch(() => !cancelled && setHasCamera(true));
    return () => {
      cancelled = true;
    };
  }, []);
  return hasCamera;
}

export function DocumentUploadField({
  eventId,
  kind,
  label,
  hint,
  value,
  onChange,
  disabled,
  optional,
  upload,
}: DocumentUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const hasCamera = useHasCamera();
  const [preview, setPreview] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    onChange(undefined);
    setIsPdf(file.type === 'application/pdf');
    setPreview(file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
    try {
      const blob = await compressImageForUpload(file);
      const key = await (upload ? upload(kind, blob) : uploadRegistrationDocument(eventId, kind, blob));
      onChange(key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al subir el archivo');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
      if (cameraRef.current) cameraRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <Label>
        {label} {optional ? <span className="text-xs font-normal text-muted-foreground">(opcional)</span> : <span className="text-destructive">*</span>}
      </Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        disabled={disabled || uploading}
      />

      {hasCamera && (
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          disabled={disabled || uploading}
        />
      )}

      <div className="flex items-center gap-3 rounded-md border border-dashed p-3">
        {preview ? (
          <img src={preview} alt={label} className="size-16 shrink-0 rounded object-cover" />
        ) : isPdf ? (
          <FileText className="size-10 shrink-0 text-muted-foreground" />
        ) : (
          <Camera className="size-10 shrink-0 text-muted-foreground" />
        )}

        <div className="min-w-0 flex-1 text-sm">
          {uploading && (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Subiendo...
            </span>
          )}
          {!uploading && value && (
            <span className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="size-4" /> Archivo cargado
            </span>
          )}
          {!uploading && !value && !error && <span className="text-muted-foreground">Foto o PDF, máx. 8 MB</span>}
          {error && <span className="text-destructive">{error}</span>}
        </div>

        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          {hasCamera && (
            <Button
              type="button"
              variant={value ? 'ghost' : 'default'}
              size="sm"
              onClick={() => cameraRef.current?.click()}
              disabled={disabled || uploading}
              aria-label={value ? 'Tomar otra foto' : undefined}
            >
              <Camera className="size-4" />
              {!value && 'Tomar foto'}
            </Button>
          )}
          <Button
            type="button"
            variant={value ? 'ghost' : 'outline'}
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || uploading}
            aria-label={value ? 'Reemplazar archivo' : undefined}
          >
            {value ? <RotateCcw className="size-4" /> : hasCamera ? 'Archivo' : 'Seleccionar'}
          </Button>
        </div>
      </div>
    </div>
  );
}
